import { Inject, Injectable } from '@angular/core';
import { AuthService, RedirectLoginOptions } from '@auth0/auth0-angular';
import { stringConstant } from './stringConstants';
import { DOCUMENT } from '@angular/common';
import { HttpService } from './http.service';
import { RefreshTokenRequest, UpdateAccessToken } from '../models/shared.model';
import { AppState } from '../models/user.dto';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import config from '../../../../capacitor.config';
import { SharedService } from './shared.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public readonly constant: typeof stringConstant;

  /**
   * Constructor
   */
  constructor(
    private httpService: HttpService,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
    private sharedService: SharedService
  ) {
    this.constant = stringConstant;
  }

  /**
   * Method for login the user by auth0
   * @param screenHintType: screen hint type for open that page
   */
  auth0Login(screenHintType: string): void {
    // Build the redirect_uri based on platform
    // For Android/iOS: use custom URL scheme
    // For Web: use window.location.origin
    const isNative = Capacitor.isNativePlatform();

    const redirect_uri = isNative
      ? `${config.appId}://${environment.auth.domain}${this.constant.capacitorCallbackPath}/${config.appId}${this.constant.slashCallback}`
      : environment.auth.authorizationParams.redirect_uri;

    // Clear Auth0's internal cache to force fresh login
    // This ensures the login screen is shown instead of using cached session
    if (typeof window !== 'undefined' && window.localStorage) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.includes(this.constant.auth0StorageKeyPrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => window.localStorage.removeItem(key));
    }

    // Prepare login options
    const loginOptions: RedirectLoginOptions<AppState> = {
      appState: {
        response_type: this.constant.token,
        target: this.constant.slashCallback,
      },
      authorizationParams: {
        redirect_uri,
        screen_hint: screenHintType,
        prompt: this.constant.login as 'login', // Force the login screen to appear, even if user has a cached session
      },
    };

    // For native platforms (Android/iOS), use Capacitor Browser plugin
    // This opens the login page in system browser (Chrome Custom Tabs on Android)
    if (isNative) {
      loginOptions.openUrl = async (url: string) => {
        await Browser.open({ url, windowName: '_self' });
      };
    }

    this.authService.loginWithRedirect(loginOptions).subscribe();
  }

  /**
   * Method for logout the user by auth0
   */

  auth0Logout(clearSessionStorage: boolean = true): void {
    if (clearSessionStorage) {
      sessionStorage.clear();
    }
    localStorage.clear();

    // Update login status to notify all subscribers (e.g., sidebar component)
    this.sharedService.setUserLoginStatus();

    // On native platforms (Android/iOS), don't redirect to logout URL
    // This prevents the logout URL from opening when called from login flow
    // Web behavior remains unchanged - redirects to logout URL as before
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) {
      this.authService.logout({
        logoutParams: {
          returnTo: this.document.location.origin,
        },
      });
    }
    // On native, just clear storage without redirecting
  }

  /**
   * Method for get updated refresh token
   * @param get the credential token of the user
   * @returns the regenerate credential token of the user
   */
  getUpdatedAccessToken(
    credentials: RefreshTokenRequest
  ): Observable<UpdateAccessToken> {
    const apiUrl = this.constant.authTokenApiUrl;

    const headers = new HttpHeaders({
      'App-Id': 'angular',
    });

    return this.httpService.postApi(apiUrl, credentials, { headers });
  }

  /**
   * Method for change the user password
   * @param changePasswordFormData - The new password data
   * @returns Observable with the response
   */
  changePassword(changePasswordFormData: any): Observable<any> {
    const apiUrl = 'api/Auth/changepassword';
    return this.httpService.postApi(apiUrl, changePasswordFormData);
  }
}
